<script setup lang="ts">

  import {
    Dialog,
    DialogScrollContent,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogHeader,
  } from '@/components/shad/ui/dialog'
  import WarnButton from '@/components/buttons/general/WarnButton.vue'
  import InformationalButton from '@/components/buttons/general/InformationalButton.vue'
  import { isCodeHostingDomain } from '@/util/links/externalLinks'
  import { AlertTriangle, Info } from '@lucide/vue'
  import { computed } from 'vue'

  interface Props {
    open: boolean
    url: string | undefined
  }
  const props = defineProps<Props>()

  const emit = defineEmits<{
    'update:open': [value: boolean]
    confirm: []
  }>()

  const parsed = computed(() => {
    if (!props.url) return null
    try {
      return new URL(props.url)
    } catch {
      return null
    }
  })

  const isCodeHosting = computed(() =>
    parsed.value ? isCodeHostingDomain(parsed.value.hostname) : false,
  )

  const onCancel = () => emit('update:open', false)
  const onConfirm = () => emit('confirm')
  </script>

  <!--
  * Worth mentioning for compliance (?) purposes: this warning will not appear when a user 
  * right clicks on a link and selects "Open link in new tab". That's a browser context event.
  -->
  <template>
    <Dialog :open="open" modal @update:open="emit('update:open', $event)">
      <DialogScrollContent class="max-w-[600px]">
        <DialogHeader>
          <DialogTitle class="font-bold flex items-center gap-2">
            <AlertTriangle v-if="isCodeHosting" class="text-warn" :size="20" />
            <Info v-else class="text-subdued" :size="20" />
            <span v-if="isCodeHosting">Warning: External code or security tool host</span>
            <span v-else>Warning: You're leaving Galvanick</span>
          </DialogTitle>
        </DialogHeader>

        <DialogDescription v-if="isCodeHosting" class="text-base leading-normal">
          The following domain is known to host code and/or security tools.
          Exercise caution when downloading or running software from an external location.
        </DialogDescription>
        <DialogDescription class="text-base leading-normal">
          If you trust this link, click Continue.
        </DialogDescription>

        <div class="my-4 p-3 bg-muted rounded font-mono text-sm break-all">
          {{ url }}
        </div>

        <DialogFooter>
          <InformationalButton @click="onCancel">Go back</InformationalButton>
          <WarnButton @click="onConfirm">Continue</WarnButton>
        </DialogFooter>
      </DialogScrollContent>
    </Dialog>
  </template>